const CUSTOM_LIMITS = Object.freeze({
  minWidth: 240,
  maxWidth: 3840,
  minHeight: 240,
  maxHeight: 4320,
  minDpr: 1,
  maxDpr: 4
});

export const DEFAULT_DEVICE_PROFILE_ID = 'iphone-modern';

const DEVICE_PROFILES = Object.freeze([
  {
    id: 'iphone-modern',
    label: 'iPhone Layout Frame',
    family: 'iPhone',
    platform: 'iOS',
    browserHint: 'Safari',
    width: 393,
    height: 852,
    dpr: 3,
    safeAreaInsets: { top: 59, right: 0, bottom: 34, left: 0 },
    cutout: { type: 'dynamic-island' },
    chromeModel: 'ios-safari-approx',
    accuracy: 'layout-simulation',
    note: 'Local layout simulation with approximate Safari UI chrome. Open the project on iOS Safari for true WebKit rendering.'
  },
  {
    id: 'pixel-modern',
    label: 'Pixel Layout Frame',
    family: 'Pixel',
    platform: 'Android',
    browserHint: 'Chrome',
    width: 412,
    height: 915,
    dpr: 2.625,
    safeAreaInsets: { top: 24, right: 0, bottom: 24, left: 0 },
    cutout: { type: 'hole-punch' },
    chromeModel: 'android-chrome-approx',
    accuracy: 'layout-simulation',
    note: 'Local layout simulation with approximate Chrome UI chrome. Open the project on Android Chrome for true device rendering.'
  },
  {
    id: 'ipad-tablet',
    label: 'iPad / Tablet Frame',
    family: 'Tablet',
    platform: 'iPadOS',
    browserHint: 'Safari',
    width: 820,
    height: 1180,
    dpr: 2,
    safeAreaInsets: { top: 24, right: 0, bottom: 20, left: 0 },
    cutout: { type: 'none' },
    chromeModel: 'tablet-safari-approx',
    accuracy: 'layout-simulation',
    note: 'Local tablet layout simulation with approximate Safari UI chrome. Verify on the target tablet browser before release.'
  },
  {
    id: 'android-tablet',
    label: 'Android Tablet Frame',
    family: 'Tablet',
    platform: 'Android',
    browserHint: 'Chrome',
    width: 800,
    height: 1280,
    dpr: 2,
    safeAreaInsets: { top: 24, right: 0, bottom: 24, left: 0 },
    cutout: { type: 'none' },
    chromeModel: 'android-tablet-approx',
    accuracy: 'layout-simulation',
    note: 'Local tablet layout simulation with approximate Chrome UI chrome. Verify on the target Android browser before release.'
  },
  {
    id: 'desktop-reference',
    label: 'Desktop Reference Frame',
    family: 'Desktop',
    platform: 'Desktop',
    browserHint: 'Current browser',
    width: 1440,
    height: 900,
    dpr: 1,
    safeAreaInsets: { top: 0, right: 0, bottom: 0, left: 0 },
    cutout: { type: 'none' },
    chromeModel: 'none',
    accuracy: 'layout-constraint',
    note: 'Viewport constraint only. Rendering still uses the current browser engine.'
  }
]);

function clamp(number, min, max) {
  return Math.min(max, Math.max(min, number));
}

function cloneSafeArea(value = {}) {
  return {
    top: Math.max(0, Math.round(Number(value.top) || 0)),
    right: Math.max(0, Math.round(Number(value.right) || 0)),
    bottom: Math.max(0, Math.round(Number(value.bottom) || 0)),
    left: Math.max(0, Math.round(Number(value.left) || 0))
  };
}

function cloneProfile(profile) {
  return {
    ...profile,
    safeAreaInsets: cloneSafeArea(profile.safeAreaInsets),
    cutout: { ...(profile.cutout || { type: 'none' }) }
  };
}

function normalizeNumber(value, fallback, min, max) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return clamp(number, min, max);
}

export function listDeviceProfiles() {
  return DEVICE_PROFILES.map(cloneProfile);
}

export function getDeviceProfile(id) {
  const profile = DEVICE_PROFILES.find((item) => item.id === id) || null;
  return profile ? cloneProfile(profile) : null;
}

export function getDefaultDeviceProfile() {
  return getDeviceProfile(DEFAULT_DEVICE_PROFILE_ID);
}

export function normalizeCustomPreviewFrame(input = {}) {
  const width = Math.round(normalizeNumber(input.width, 390, CUSTOM_LIMITS.minWidth, CUSTOM_LIMITS.maxWidth));
  const height = Math.round(normalizeNumber(input.height, 844, CUSTOM_LIMITS.minHeight, CUSTOM_LIMITS.maxHeight));
  const dpr = Math.round(normalizeNumber(input.dpr, 2, CUSTOM_LIMITS.minDpr, CUSTOM_LIMITS.maxDpr) * 100) / 100;
  const label = String(input.label || 'Custom Screen').trim().slice(0, 40) || 'Custom Screen';

  return {
    id: 'custom',
    label,
    width,
    height,
    dpr,
    safeAreaInsets: cloneSafeArea(input.safeAreaInsets),
    cutout: { type: 'none' },
    chromeModel: 'custom',
    accuracy: 'layout-constraint',
    note: 'Custom viewport constraint. Rendering still uses the current browser engine.'
  };
}

export function profileToPreviewFrame(profile) {
  const source = profile || getDefaultDeviceProfile();
  return {
    id: source.id,
    label: source.label,
    width: Math.round(source.width),
    height: Math.round(source.height),
    dpr: Number(source.dpr) || 1,
    platform: source.platform,
    browserHint: source.browserHint,
    safeAreaInsets: cloneSafeArea(source.safeAreaInsets),
    cutout: { ...(source.cutout || { type: 'none' }) },
    chromeModel: source.chromeModel || 'none',
    accuracy: source.accuracy || 'layout-simulation',
    note: source.note || ''
  };
}

export function swapPreviewFrameOrientation(frame) {
  const normalized = normalizeCustomPreviewFrame(frame);
  return normalizeCustomPreviewFrame({
    ...normalized,
    width: normalized.height,
    height: normalized.width,
    label: normalized.label
  });
}

export { CUSTOM_LIMITS };
